import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
}                                                                                    from '@angular/core';
import {BehaviorSubject, combineLatest, fromEvent, merge, Observable, Subscription}  from 'rxjs';
import {
  ApGridPagerType,
  IGridPagerData,
  IWizardChoiceData,
  IWizardChoiceDataValue,
  MapViewCurrentMenu,
  MapViewMode,
  SidebarMode
}                                                                                    from '../../ap-interface';
import {CompositeFilterDescriptor, FilterDescriptor, process, SortDescriptor, State} from '@progress/kendo-data-query';
import {
  ApGridPagerComponent
}                                                                                    from '../../ap-core/components/ap-grid-pager/ap-grid-pager.component';
import {animate, state, style, transition, trigger}                                  from '@angular/animations';
import {
  ApDynformsConfigFieldset
}                                                                                    from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                                                    from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsConfigCheckbox
}                                                                                    from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {
  ICropRotationMassEdit
}                                                                                    from '../interfaces/ap-crop-rotation-mass-edit.interface';
import {
  CropRotationOperations
}                                                                                    from '../enums/crop-rotation-operations.enum';
import {
  ApDynformsConfigPlaceholder
}                                                                                    from '../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {
  CampaignYearStore
}                                                                                    from '../../stores/login/campaignyear.store';
import {
  TranslationStore
}                                                                                    from '../../stores/translation/translation.store';
import {APP_CONFIGURATION}                                                           from '../../ap-core/config';
import {
  MapViewStore
}                                                                                    from '../../stores/layout/mapview.store';
import {
  FieldStore
}                                                                                    from '../../stores/farm/field.store';
import {
  ApDynformsConfigNumerictextbox
}                                                                                    from '../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {map, skip}                                                                   from 'rxjs/operators';
import {
  ApDynformsValidator
}                                                                                    from '../../ap-dynforms/ap-dynforms-validator';
import {FormGroup, ValidatorFn}                                                      from '@angular/forms';
import {
  ApCropRotationHeaderComponent
}                                                                                    from './header/ap-crop-rotation-header.component';
import {ApMapInstance}                                                               from '../../ap-map';
import {
  CampaignYearService
}                                                                                    from '../../services/data/campaign-year.service';
import {
  ModalDialogStore
}                                                                                    from '../../stores/dialog/modal.dialog.store';
import {
  IModalDialogResult
}                                                                                    from '../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {
  ModalDialogPresets
}                                                                                    from '../../stores/dialog/modal.dialog.presets';
import {
  nameOf
}                                                                                    from '../../ap-core/utils/ap-name-of';
import IFieldCropRotation = Data.FieldManagement.IFieldCropRotation;
import ICropTypes = Data.BaseData.ICropTypes;
import IGuid = System.IGuid;
import ICropRotationCell = Data.FieldManagement.ICropRotationCell;

/**
 * Width of sidebar when fully open
 */
const SIDEBAR_WIDTH_FULL = 199.219;
/**
 * Width of the sidebar if it is open but only the symbols are visible
 */
const SIDEBAR_WIDTH_SMALL = 40;
/**
 * Width of the field view for the Grid crop rotation
 */
const FIELD_LIST_WIDTH = 449;
/**
 * Maximum width a cell may have
 */
const MAX_COLUMN_WIDTH = 178;
/**
 * Width of the placeholder for the right scrollbar
 */
const RIGHT_SCROLL_PLACEHOLDER = 30;

/**
 * Draws a grid which represents the crop rotation of a farm from the current Campaign Year + 5 years and - 1 year.
 *
 * @example
 * <ap-crop-rotation-grid [items]="cropRotations$ | async"
 * [loading]="cropRotationsLoading$ | async"
 * [saving]="cropRotationSaving$ | async"
 * [availableCropTypes]="cropTypes$ | async"
 * [currentYear]="(currentYear$ | async).Year"
 * [yearInterval]="[-1, 0, 1]"
 * [sidebarChange$]="sidebarViewStore.listenSidebarMode"
 * [mapViewChange$]="mapViewStore.listenMapViewMode$"
 * (saveCell)="onSaveCell($event)"
 * (getFocus)="onCellGetFocus($event)"
 * (lostFocus)="onCellLostFocus($event)"
 * (massEditChange)="onMassEditChange($event)"
 * (massEditRemoveMainCrop)="onMassEditRemoveMainCrop($event)"
 * (massEditRemoveSecondCrop)="onMassEditRemoveSecondCrop($event)"
 * ></ap-crop-rotation-grid>
 */
@Component({
  selector: 'ap-crop-rotation-grid',
  templateUrl: './ap-crop-rotation-grid.component.html',
  animations: [
    trigger('resizeDataHeight', [
      state('in', style({height: 'calc(100% - 171px)'})),
      state('in-with-form', style({height: 'calc(100% - 438px)'})),
      state('out', style({height: 'calc(100% - 135px)'})),
      transition('in => out', animate(300)),
      transition('out => in', animate('300ms 0.2s')),
      transition('out => in-with-form', animate('300ms 0.2s')),
      transition('in => in-with-form', animate('300ms 0.2s')),
      transition('in-with-form => in', animate(300)),
      transition('in-with-form => out', animate(300)),
    ])
  ]
})
export class ApCropRotationGridComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(ApCropRotationHeaderComponent) headerComponent: ApCropRotationHeaderComponent;
  /**
   * the number of decimal places
   */
  @Input() public numericRound = 2;
  /**
   * The data source of the Grid crop rotation
   */
  @Input() public items: IFieldCropRotation[];
  /**
   * The Loading State of the source when it loads from async
   */
  @Input() public loading: boolean;
  /**
   * Available CropTypes of the Farm used for Selection and Filter
   */
  @Input() public availableCropTypes: ICropTypes[];
  @Input() public allCropTypes: ICropTypes[];
  /**
   * the CampaignYear of the Farm that is currently loaded
   */
  @Input() public currentYear = 2019;
  /**
   * the Years to Display represented ba an Array
   *
   * @example
   * This Displays the Years 2018, 2019, 2020 when the current Campaign Year is 2019
   * [-1, 0, 1]
   */
  @Input() public yearInterval: number[];
  /**
   * The Store Reference when MapViewMode is changing
   */
  @Input() public mapViewChange$: Observable<MapViewMode>;
  /**
   * The Store Reference when the SideBarMode is changing
   */
  @Input() public sidebarChange$: Observable<SidebarMode>;
  /**
   * A List of Cells that was currently saving
   */
  @Input() public saving: ICropRotationCell[] = [];
  /**
   * Event for searched text
   */
  @Input() public searchText: BehaviorSubject<string> = null;

  /**
   * Triggers when clear filters button is clicked on the toolbar
   */
  @Input() public clearAllFilters: BehaviorSubject<boolean> = null;

  /**
   * Triggers when a Cell begins to save
   */
  @Output() public saveCell = new EventEmitter<ICropRotationCell>();
  /**
   * Triggers when any of the items are selected
   */
  @Output() public selectedItems = new EventEmitter<IFieldCropRotation[]>();
  /**
   * Triggers when a MassEdit begins to save Cells for changing Values
   */
  @Output() public massEditChange = new EventEmitter<ICropRotationMassEdit<ICropRotationCell>>();
  /**
   * Triggers when a MassEdit begins to save Cells to remove the MainCrop
   * that means the complete Crop was deleted from FieldCrops
   */
  @Output() public massEditRemoveMainCrop = new EventEmitter();
  /**
   * Triggers when a MassEdit begins to save Cells to remove the SecondCrop
   */
  @Output() public massEditRemoveSecondCrop = new EventEmitter();
  /**
   * Triggers when a Cell gets the Focus for edit
   */
  @Output() public getFocus = new EventEmitter<[string, number]>();
  /**
   * Triggers when a Cell lost the edit Focus
   */
  @Output() public lostFocus = new EventEmitter<[string, number]>();

  public values: IWizardChoiceDataValue[];

  public dataWrapperHeight = 0;
  /**
   * A List of Wizard Options
   */
  public listMassEditing$ = new BehaviorSubject<IWizardChoiceData[]>([]);
  /**
   * The Dynforms Configuration for the MassEdit Form
   */
  public formConfig: ApDynformsConfigFieldset[] = null;
  /**
   * Mass edit form validators (if more than one control needs validation at one time)
   */
  public formValidators: ApDynformsValidator<ValidatorFn>[] = [
    new ApDynformsValidator({
      validator: ApCropRotationGridComponent.yieldRequiredIfCropIsSet,
      errorKey: 'Yield__required'
    }),
  ];
  /**
   * The Kendo Filter Description for the Data
   */
  public filter: CompositeFilterDescriptor = null;
  /**
   * The Animation State of the MultiSelect (MassEdit)
   */
  public currentMultiSelect = 'out';
  /**
   * The length of the Items at one Page
   */
  public pageSize = APP_CONFIGURATION.GridPageSize;
  /**
   * The currently displayed Page
   */
  public currentPage = 1;
  /**
   * The sorted and filtered Items for the current Page
   */
  public itemView: IFieldCropRotation[] = [];
  /**
   * The selected Fields to processing the MassEdit
   */
  public selectedFields: IGuid[] = [];
  /**
   * The current calculated Cell Width
   */
  public columnWidth = MAX_COLUMN_WIDTH;
  /**
   * the Items filtered and sorted without Paging
   */
  public itemsFilteredNoPaging: IFieldCropRotation[] = [];
  public fullItems: IFieldCropRotation[] = [];
  @ViewChild('dataWrapper', {static: true}) private _dataWrapper: ElementRef;
  @ViewChild('pager', {static: true}) private _pager: ApGridPagerComponent;
  private windowResize = fromEvent(window, 'resize');
  private _subscriptions: Subscription[] = [];
  private _sort: SortDescriptor[] = [
    {field: nameOf<IFieldCropRotation>('FieldName'), dir: 'asc'},
    {field: nameOf<IFieldCropRotation>('FieldNumber'), dir: 'asc'},
    {field: nameOf<IFieldCropRotation>('FieldSubNumber'), dir: 'asc'}
  ];
  private _lastSidebarMode = SidebarMode.OPEN;
  private _lastMapViewMode = MapViewMode.HIDE;
  private _state: State = {
    skip: 0,
    take: this.pageSize,
    sort: this._sort
  };
  private dataWrapperObserver = new ResizeObserver(entries => {
    for (const entry of entries) {
      this.dataWrapperHeight = entry.contentRect.height;
    }
  });
  private pagerDataSubscription: Subscription = null;
  private mainCropMassEditChanged$ = new BehaviorSubject<ICropTypes | undefined>(undefined);
  private secondCropMassEditChanged$ = new BehaviorSubject<ICropTypes | undefined>(undefined);
  private _massEditPagerData: IGridPagerData;

  /**
   * Validations for mass edit
   * @param frm
   * @private
   */
  private static yieldRequiredIfCropIsSet(frm: FormGroup): { missingYield: boolean } | null {
    const mainCrop = frm.get('MainCrop')?.value;
    const mainCropYieldControl = frm.get('MainCropYield');
    const mainCropYield = mainCropYieldControl?.value;
    const secondCrop = frm.get('SecondCrop')?.value;
    const secondCropYieldControl = frm.get('SecondCropYield');
    const secondCropYield = secondCropYieldControl?.value;
    const validationError = {missingYield: true};
    // Validate if yield is present when crop is selected.
    // This validation is done on form level because 2 form controls are involved.
    // Besides that, the error is set at the control directly to avoid having
    // an error message (errorkey) being shown at the control
    if (secondCrop?.Id > 0 && !(parseFloat(secondCropYield) >= 0)) {
      secondCropYieldControl?.setErrors(validationError);
      return validationError;
    }
    if (mainCrop?.Id > 0 && !(parseFloat(mainCropYield) >= 0)) {
      mainCropYieldControl?.setErrors(validationError);
      return validationError;
    }
    return null;
  }

  constructor(private translationStore: TranslationStore,
              private campaignYearStore: CampaignYearStore,
              private mapViewStore: MapViewStore,
              private fieldStore: FieldStore,
              private modalDialogStore: ModalDialogStore,
              private modalDialogPresets: ModalDialogPresets,
              private campaignYearService: CampaignYearService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.dataWrapperObserver.observe(this._dataWrapper.nativeElement);
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe((d) => {
      if (d) {
        this.currentYear = d.Year;
      }
    }));
    this._subscriptions.push(this.sidebarChange$.subscribe(d => this._lastSidebarMode = d));
    this._subscriptions.push(this.mapViewChange$.subscribe(d => this._lastMapViewMode = d));
    this._subscriptions.push(merge(this.mapViewChange$, this.sidebarChange$, this.windowResize).subscribe(() => {
      if (this.yearInterval.length > 0) {
        this.setAvailableInterval(window.innerWidth);
      }
    }));
    // Update grid selection based on clicked fields in a map
    this._subscriptions.push(ApMapInstance.onFieldClicked.subscribe(field => {
      if (!(field?.Id?.toString()?.length > 0)) {
        return;
      }
      const updatedSelectedFields = this.selectedFields?.slice() ?? [];
      const selectedFieldIndex = this.selectedFields?.FindIndex(id => id?.toString() === field?.Id?.toString());
      if (selectedFieldIndex >= 0) {
        updatedSelectedFields.RemoveAt(selectedFieldIndex);
      } else {
        updatedSelectedFields.Add(field?.Id?.toString());
      }
      this.onSelectionChange(updatedSelectedFields);
      this.changeDetectorRef.detectChanges();
    }));
    this._subscriptions.push(this.searchText.subscribe(search => {
      this.filterMainItems(search);
    }));
    this._subscriptions.push(this.clearAllFilters.subscribe(clear => {
      if (clear) {
        this.clearAllFiltersLogic();
      }
    }));
    this.setWizardOptions();
    this._subscriptions.push(this.modalDialogStore.Listen(s => s.result).pipe(skip(1)).subscribe(c => {
      this._modalDialogOkClicked(c);
    }));
  }

  private clearAllFiltersLogic(): void {
    // Filter
    this._state.filter = {
      logic: 'and',
      filters: [],
    } as CompositeFilterDescriptor;

    this.itemsFilteredNoPaging = process(this.items, {
      sort: this._state.sort,
      filter: this._state.filter
    }).data;
    // Pagination
    this.itemView = process(this.itemsFilteredNoPaging, {
      skip: this._state.skip,
      take: this._state.take,
    }).data;
    this.fullItems = [...this.items];
    this.headerComponent?.clearAllFiltersInFilterComponents();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.items && changes?.items?.currentValue) {
      this.mapItems();
    }
  }

  public ngOnDestroy(): void {
    this._subscriptions?.forEach(x => x?.unsubscribe());
    this.pagerDataSubscription?.unsubscribe();
  }

  /**
   * pager selected a new Page
   */
  public onPageChange(pageNumber: number): void {
    this.currentPage = pageNumber;
    this.mapItems();
  }

  /**
   * a Sort was Triggers
   */
  public onSort(descriptors: SortDescriptor[]): void {
    this._sort = descriptors;
    this.mapItems();
  }

  /**
   * fires when the Filter was changed
   */
  public onFilter(filterDescriptor: CompositeFilterDescriptor): void {
    this.filter = filterDescriptor;
    this.mapItems();
  }

  /**
   * when the MassEdit was applied
   */
  public onMassEditApply(e: IGridPagerData): void {
    const fieldsAmount = this.selectedFields.length;
    if (fieldsAmount > 10) {
      this._massEditPagerData = e;
      this.modalDialogStore.setModalDialogData(this.modalDialogPresets.ExistRelatedDataDialog(
        {
          confirmKey: 'confirmMassEdit',
          message: this.translationStore.FindTranslationForSelectedLanguage(
            'Global_Message_Edit_Fields_Number', [fieldsAmount.toString()])
        }));
    } else {
      this.applyMassEditAction(e);
    }
  }

  private applyMassEditAction(e: IGridPagerData): void {
    switch (e.object.id) {
      case CropRotationOperations.CHANGE:
        this.massEditChange.emit({
          Ids: this.selectedFields,
          Value: {
            FieldId: null,
            Year: this.currentYear + e.value.id,
            YearIndex: e.value.id,
            MainCrop: e.form && e.form.MainCrop ? e.form.MainCrop.Id : undefined,
            MainCropYield: e.form && e.form.MainCropYield > 0 ? e.form.MainCropYield : undefined,
            MainCropStraw: e.form && e.form.MainCropStraw ? e.form.MainCropStraw : false,
            SecondCrop: e.form && e.form.SecondCrop ? e.form.SecondCrop.Id : undefined,
            SecondCropYield: e.form && e.form.SecondCropYield > 0 ? e.form.SecondCropYield : undefined,
          }
        });
        break;
      case CropRotationOperations.REMOVE_MAIN_CROP:
        this.massEditRemoveMainCrop.emit({
          Ids: this.selectedFields,
          Value: {
            FieldId: null,
            Year: this.currentYear + e.value.id,
            YearIndex: e.value.id,
            MainCrop: null,
            MainCropYield: null,
            MainCropStraw: false
          }
        });
        break;
      case CropRotationOperations.REMOVE_SECOND_CROP:
        this.massEditRemoveSecondCrop.emit({
          Ids: this.selectedFields,
          Value: {
            FieldId: null,
            Year: this.currentYear + e.value.id,
            YearIndex: e.value.id,
            SecondCrop: null,
            SecondCropYield: null
          }
        });
        break;
    }
  }

  /**
   * fires when the selection was changed
   */
  public onSelectionChange(e: IGuid[]): void {
    this.selectedFields = e;
    this.selectedItems.emit(this.items.filter(item => e.some(selectedId => item.Id === selectedId)));
    if (!e || e.length < 1) {
      this.onMultiSelectionChange(null);
    }
    if (this.mapViewStore.getMapViewMode() !== MapViewMode.HIDE && this.mapViewStore.getMapViewCurrentMenu() === MapViewCurrentMenu.MAP) {
      this.fieldStore.changeSelectedField(this.selectedFields.map(_ => _?.toString()));
    }
  }

  /**
   * when the Multi Select Option was changed
   */
  public onMultiSelectionChange(e: IWizardChoiceData): void {
    this.mainCropMassEditChanged$.next(undefined);
    this.secondCropMassEditChanged$.next(undefined);
    if (!e) {
      this.formConfig = null;
      this.currentMultiSelect = this.selectedFields && this.selectedFields.length > 0 ? 'in' : 'out';
      return;
    }
    switch (e.id) {
      case CropRotationOperations.CHANGE:
        this.formConfig = [
          new ApDynformsConfigFieldset({
            key: 'grid',
            legend: this.translationStore.FindTranslationForSelectedLanguage('Settings__Lbl_Menu_Croptypes'),
            config: [
              new ApDynformsConfigComboBox({
                key: 'MainCrop',
                valueField: 'Id',
                textField: 'Description',
                valuePrimitive: false,
                label: this.translationStore.FindTranslationForSelectedLanguage('MainCrop'),
                value: null,
                options: this.availableCropTypes,
                validators: []

              }),
              new ApDynformsConfigNumerictextbox({
                key: 'MainCropYield',
                label: this.translationStore.FindTranslationForSelectedLanguage('Global__YieldUnit'),
                min: 0,
                validators: [],
                value$: this.mainCropMassEditChanged$.pipe(map((cropType) => {
                  return cropType?.ExpectedYield;
                }))
              }),
              new ApDynformsConfigCheckbox({
                key: 'MainCropStraw',
                label: this.translationStore.FindTranslationForSelectedLanguage('Nutrients_Pages__StrawRemoved'),
                value: false,
              }),
              new ApDynformsConfigPlaceholder(),
              new ApDynformsConfigComboBox({
                key: 'SecondCrop',
                valueField: 'Id',
                textField: 'Description',
                valuePrimitive: false,
                label: this.translationStore.FindTranslationForSelectedLanguage('SecondCrop'),
                value: null,
                options: this.availableCropTypes,
                validators: [],
              }),
              new ApDynformsConfigNumerictextbox({
                key: 'SecondCropYield',
                label: this.translationStore.FindTranslationForSelectedLanguage('Global__Yield_Removal_Unit'),
                min: 0,
                validators: [],
                value$: this.secondCropMassEditChanged$.pipe(map((cropType) => {
                  return cropType?.Id > 0 ? 0 : undefined; // if crop is set the yield will be set to 0 - otherwise it is not defined
                }))
              }),
            ],
          }),
        ];
        this.currentMultiSelect = 'in-with-form';
        break;
      case CropRotationOperations.REMOVE_MAIN_CROP:
      case CropRotationOperations.REMOVE_SECOND_CROP:
        this.formConfig = null;
        this.currentMultiSelect = 'in';
        break;
    }
  }

  private mapItems(): void {
    this.filterMainItems(this.searchText.getValue());
  }

  private filterMainItems(searchText = ''): void {
    this._state.skip = (this.currentPage - 1) * this.pageSize;
    this._state.take = this.pageSize;
    this._state.sort = this._sort;
    if (this.filter) {
      this._state.filter = this.filter;
    }
    const fieldNameSearchText = {
      field: 'FieldName',
      value: searchText,
      operator: 'contains',
      ignoreCase: true,
    } as FilterDescriptor;
    const fieldNumberSearchText = {
      field: 'FieldNumber',
      value: searchText,
      operator: 'contains',
      ignoreCase: true,
    } as FilterDescriptor;
    const fieldSubNumberSearchText = {
      field: 'FieldSubNumber',
      value: searchText,
      operator: 'contains',
      ignoreCase: true,
    } as FilterDescriptor;
    const fullTextFilters = {
      logic: 'or',
      filters: [fieldNameSearchText, fieldNumberSearchText, fieldSubNumberSearchText]
    } as CompositeFilterDescriptor;

    if (!this._state.filter) {
      this._state.filter = {
        logic: 'and',
        filters: [fullTextFilters],
      } as CompositeFilterDescriptor;
    } else {
      const f = this._state.filter as CompositeFilterDescriptor;
      const cofIdx = f.filters.FindIndex(_ => _['logic'] === 'or' && Array.isArray(_['filters']) && _['filters'].length === 3 &&
        _['filters'].FindAll(__ => __['field'] === fieldNameSearchText.field ||
          __['field'] === fieldNumberSearchText.field ||
          __['field'] === fieldSubNumberSearchText.field).length === 3);
      if (cofIdx >= 0) {
        this._state.filter.filters[cofIdx] = fullTextFilters;
      } else {
        this._state.filter.filters.Add(fullTextFilters);
      }
    }
    this.fullItems = [...this.items];
    // Filter
    this.itemsFilteredNoPaging = process(this.items, {
      sort: this._state.sort,
      filter: this._state.filter,
    }).data;
    // Pagination
    this.itemView = process(this.itemsFilteredNoPaging, {
      skip: this._state.skip,
      take: this._state.take,
    }).data;
  }

  private setAvailableInterval(value: number): void {
    // Sidebar Open → 199.219
    // Sidebar Half → 40
    const sidebarOffset = this._lastSidebarMode === SidebarMode.OPEN ?
      SIDEBAR_WIDTH_FULL : this._lastSidebarMode === SidebarMode.HALF ?
        SIDEBAR_WIDTH_SMALL : 0;
    const mapWidthHalf = (value - sidebarOffset) / 2;
    const mapOffset = this._lastMapViewMode === MapViewMode.HIDE ?
      0 : this._lastMapViewMode === MapViewMode.NORMAL ?
        mapWidthHalf : 0;
    value = value - sidebarOffset - mapOffset - FIELD_LIST_WIDTH - RIGHT_SCROLL_PLACEHOLDER - 10;
    let anzColumns = Math.floor(value / MAX_COLUMN_WIDTH);
    // we want to display one cell!
    anzColumns = anzColumns < 1 ? 1 : anzColumns;
    this.columnWidth = Math.floor(value / anzColumns - 1);
    const tmp = [];
    for (let i = 0; i < anzColumns; i++) {
      tmp.push(i - 1);
    }
    this.yearInterval = tmp;
    this.setWizardOptions();
  }

  private setWizardOptions(): void {
    if (this.pagerDataSubscription) {
      return;
    }
    this.pagerDataSubscription = combineLatest([
      this.selectedItems,
      this.campaignYearStore.SelectedCampaignYear$
    ]).subscribe(([selectedItems, year]) => {
      const yearValues: IWizardChoiceDataValue[] = [];
      for (let i = -1; i < 6; i++) {
        const iterationYear = year.Year + i;
        yearValues.push({
          id: i,
          label: iterationYear.toString(),
          disabled: selectedItems.some(x => {
            const campaignYearRange = this.campaignYearService.getCampaignYearRange(iterationYear);
            return !this.campaignYearService.isCampaignYearIntersects(new Date(x.ValidFrom), new Date(x.ValidTo), campaignYearRange);
          })
        });
      }
      const preSelectedItemId = 0;
      this.values = yearValues;
      this.listMassEditing$.next([
        {
          id: CropRotationOperations.CHANGE,
          name: this.translationStore.FindTranslationForSelectedLanguage('Global__Edit'),
          type: ApGridPagerType.DropDown,
          values: yearValues,
          value: preSelectedItemId
        },
        {
          id: CropRotationOperations.REMOVE_MAIN_CROP,
          name: this.translationStore.FindTranslationForSelectedLanguage('CropRotation__MainCrop_remove'),
          type: ApGridPagerType.DropDown,
          values: yearValues,
          value: preSelectedItemId
        },
        {
          id: CropRotationOperations.REMOVE_SECOND_CROP,
          name: this.translationStore.FindTranslationForSelectedLanguage('CropRotation__SecondCrop_remove'),
          type: ApGridPagerType.DropDown,
          values: yearValues,
          value: preSelectedItemId
        }
      ]);
    });
  }

  private _modalDialogOkClicked(result: IModalDialogResult): void {
    if (result?.key === 'confirmMassEdit') {
      this.applyMassEditAction(this._massEditPagerData);
    }
  }

  public onPageSizeChanged(pageSize: number): void {
    if (!this._pager || !this.itemView || !this.items) {
      return;
    }
    this.pageSize = pageSize;
    this._pager.pageSize = pageSize;
    this.filterMainItems();
  }

  public onFormValueChanges(v: { control: string; value: any }): void {
    if (v.control === 'MainCrop') {
      this.mainCropMassEditChanged$.next(v.value);
    }
    if (v.control === 'SecondCrop') {
      this.secondCropMassEditChanged$.next(v.value);
    }
  }
}
